import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SingleActionDialogComponent } from '../components/dialogs/single-action-dialog/single-action-dialog.component';
import { AuthService } from './auth.service';
import { SharedStorageService } from './shared-storage.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private readonly PLAN_CALCULATION_TIME = 30 * 60 * 1000; // 30分鐘 (方案演算)
  // private readonly PLAN_CALCULATION_TIME = 10 * 1000; // 10秒 (test)
  private readonly IDLE_TIME = 30 * 60 * 1000; // 30分鐘
  // private readonly IDLE_TIME = 10 * 1000; // 10秒 (test)
  private idleTimer: any;
  private isActive = false;

  constructor(private injector: Injector, private ngZone: NgZone) {
    console.log('IdleTimeoutService has been created');
  }

  /**
   * 開始監控用戶閒置狀態
   */
  startWatching(): void {
    console.log('startWatching has been called');
    if (this.isActive) {
      console.log('IdleTimeoutService is already active');
      return;
    }
    
    this.isActive = true;
    this.resetTimer();
    this.addEventListeners();
  }

  /**
   * 停止監控
   */
  stopWatching(): void {
    console.log('stopWatching has been called');
    this.isActive = false;
    this.clearTimer();
    this.removeEventListeners();
    console.log('stopWatching is done');
  }

  /**
   * 重置計時器
   */
  private resetTimer(): void {
    this.clearTimer();
    
    this.ngZone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        console.log('Times up!!!!!!! handleIdleTimeout is about to be called');
        this.ngZone.run(() => {
          this.handleIdleTimeout();
        });
      }, this.IDLE_TIME);
    });
  }

  /**
   * 清除計時器
   */
  private clearTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * 處理閒置超時
   */
  private handleIdleTimeout(): void {
    console.log('start handleIdleTimeout');
    
    const router = this.injector.get(Router);
    const dialog = this.injector.get(MatDialog);
    const translateService = this.injector.get(TranslateService);
    const authService = this.injector.get(AuthService);
    const sharedStorageService = this.injector.get(SharedStorageService);

    // 停止監控
    this.stopWatching();

    // 獲取 token
    let accessToken: string | null = null;
    if (typeof window !== 'undefined') {
      accessToken = localStorage.getItem('accessToken');
      // localStorage.removeItem('accessToken');
      // console.log('accessToken has been removed');
      authService.performLogout().subscribe({
        next: (res) => {
          authService.clearToken();
          console.log('登出 API 成功:', res);
        },
        error: (error) => {
          authService.clearToken(); //避免閒置超時+異地登入會進入無限登出循環
          console.error('登出 API 失敗:', error);
        }
      });
    }

    // 導航到首頁
    router.navigate([''], { replaceUrl: true });

    // 顯示閒置登出提示對話框
    const dialogRef = dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: translateService.instant('login-register.idle-logout.title'),
        message: translateService.instant('login-register.idle-logout.message'),
        showCloseButton: false,
        buttonText: translateService.instant('common.button.i-understood'),
        action: () => {
          // 導航到首頁
          console.log('Dialog close button clicked');
          window.location.reload();
          dialogRef.close();
        },
      },
    });

    console.log('handleIdleTimeout is done');
  }

  /**
   * 用戶活動事件處理
   */
  private onUserActivity = (): void => {
    if (this.isActive) {
      this.resetTimer();
    }
  };

  /**
   * 添加事件監聽器
   */
  private addEventListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.onUserActivity, true);
    });
  }

  /**
   * 移除事件監聽器
   */
  private removeEventListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, this.onUserActivity, true);
    });
  }

  /**
   * 計算 30 分鐘
   */
  startCalculateIdleTime(): number {
    const startTime = Date.now();
    console.log('方案演算開始計時:', new Date(startTime));
    return startTime;

  }

  /**
   * 檢查方案演算是否超過 30 分鐘
   * @param calculationStartTime 方案演算開始時間
   * @returns 是否超過 30 分鐘
   */
  isPlanCalculationExpired(calculationStartTime: number): boolean {
    const currentTime = Date.now();
    const elapsedTime = currentTime - calculationStartTime;
    const remainingTime = this.PLAN_CALCULATION_TIME - elapsedTime;
    const isExpired = elapsedTime > this.PLAN_CALCULATION_TIME;
    
    if (isExpired) {
      console.log('方案演算已超過 30 分鐘');
    } else {
      console.log('下載時間剩餘(秒)：', Math.floor(remainingTime / 1000));
    }
    
    return isExpired;
  }
}